import { EMPTY_STRING } from '@rotomeca/utils';
import { BnumConfig } from '../../../core/utils/BnumConfig';
import BnumElement from '../../bnum-element';
import { HTMLBnumCardElement } from '../card/bnum-card';
import { HTMLBnumCardItemMail } from '../../molecules/card-item-mail/bnum-card-item-mail';
import { Nullable } from '@rotomeca/utils';
import style from './bnum-card-email.less';
import { HTMLBnumCardList } from '../../molecules/card-list/bnum-card-list';
import { HTMLBnumCardItem } from '../../molecules/card-item/bnum-card-item';
import { HTMLBnumCardTitle } from '../../molecules/card-title/bnum-card-title';
import { TAG_CARD_EMAIL } from '../../../core/utils/constants/tags';
import { Attr, Data, Define } from '../../../core/decorators';
import { Observe } from '../../../core/decorators/observe';
import { Listener } from '../../../core/decorators/listener';
import {
	ATTRIBUTE_DATA_URL,
	ATTRIBUTE_LOADING,
	CHANGE_EVENT,
	ID_CARD_ITEM_NO_ELEMENTS,
	ID_CARD_TITLE,
} from './bnum-card-email.internal/constants';
import { onElementChangedInitializer } from './bnum-card-email.internal/listeners';
import {
	HTMLBnumCardEmailUi,
	OnElementChangedEvent,
} from './bnum-card-email.internal/types';
import { ATTRIBUTE_DATA_DATE } from 'components/molecules/card-item-mail/bnum-card-item-mail.internal/constants';
import { UI } from 'core/decorators/ui';
import { RenderFrame } from 'core/decorators/defer';
import { EVENT_DEFAULT } from 'core/utils/constants/constants';

//#region Global constants
const TEXT_LAST_MAILS = BnumConfig.Get('local_keys')!.last_mails!;
const TEXT_NO_MAILS = BnumConfig.Get('local_keys')!.no_mails!;
const EVENT_URL_TITLE_CLICK = 'bnum-card-email:title:url.click';
//#endregion Global constants

//#region Template
const TEMPLATE = (
	<HTMLBnumCardElement>
		<HTMLBnumCardTitle id={ID_CARD_TITLE} slot="title" data-icon="mail">
			{TEXT_LAST_MAILS}
		</HTMLBnumCardTitle>
		<HTMLBnumCardList>
			<slot></slot>
			<HTMLBnumCardItem id={ID_CARD_ITEM_NO_ELEMENTS} disabled hidden>
				{TEXT_NO_MAILS}
			</HTMLBnumCardItem>
		</HTMLBnumCardList>
	</HTMLBnumCardElement>
);
//#endregion Template

/**
 * Organisme qui permet d'afficher simplement une liste de mails dans une carte.
 *
 * @category Card
 *
 * @structure Avec des éléments
 * <bnum-card-email>
 * <bnum-card-item-mail data-date="2025-10-31 11:11" data-subject="Sujet ici AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA" data-sender="Expéditeur ici AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA">
 * </bnum-card-item-mail>
 * <bnum-card-item-mail read data-date="2025-10-31 11:11" data-subject="Sujet ici" data-sender="Expéditeur ici">
 * </bnum-card-item-mail>
 * <bnum-card-item-mail data-date="now">
 * <span slot="subject">Sujet par défaut</span>
 * <span slot="sender">Expéditeur par défaut</span>
 * </bnum-card-item-mail>
 * </bnum-card-email>
 *
 * @structure Sans éléments
 * <bnum-card-email>
 * </bnum-card-email>
 *
 * @structure Avec une url
 * <bnum-card-email data-url="#">
 * </bnum-card-email>
 *
 * @slot (default) - Contenu des éléments de type HTMLBnumCardItemMail.
 *
 * @attr {string | undefined} (optional) data-url - Ajoute une url au titre. Ne rien mettre pour que l'option "url" du titre ne s'active pas.
 * @attr {string | undefined} (optional) loading - Si présent, affiche le mode loading.
 *
 * @event {CustomEvent<HTMLBnumCardItemMail[]>} bnum-card-email:change - Déclenché lorsque les éléments changent (ajout/suppression).
 *
 * @cssvar {block} --bnum-card-email-display - Définit le display du composant. Par défaut à "block".
 */
@Define({ tag: TAG_CARD_EMAIL, styles: style, template: TEMPLATE })
@Observe(ATTRIBUTE_LOADING)
export class HTMLBnumCardEmail extends BnumElement {
	//#region Private fields
	#_isSorting: boolean = false;
	#_card: Nullable<HTMLBnumCardElement> = null;

	//#endregion Private fields

	//#region Getters/Setters
	@UI({
		cardTitle: `#${ID_CARD_TITLE}`,
		slot: 'slot',
		noElements: `#${ID_CARD_ITEM_NO_ELEMENTS}`,
	})
	accessor #_ui!: HTMLBnumCardEmailUi;

	/**
	 * Déclenché lorsque les éléments changent (ajout/suppression).
	 */
	@Listener(onElementChangedInitializer)
	accessor onElementChanged!: OnElementChangedEvent;

	@Attr()
	accessor loading: boolean = false;

	@Data() accessor #_url: string = EMPTY_STRING;

	get #_cardPart(): HTMLBnumCardElement {
		if (this.#_card === null) {
			this.#_card =
				this.querySelector?.(HTMLBnumCardElement.TAG) ??
				this.shadowRoot?.querySelector?.(HTMLBnumCardElement.TAG) ??
				null;
		}
		return this.#_card!;
	}
	//#endregion Getters/Setters

	//#region Lifecycle
	constructor() {
		super();
	}

	protected _p_attach(): void {
		if (this.#_url !== EMPTY_STRING) {
			this.#_ui.cardTitle.url = this.#_url;
			this.#_ui.cardTitle.onurlclick.add(EVENT_DEFAULT, (e) => {
				this.trigger(EVENT_URL_TITLE_CLICK, { inner:e }, { bubbles:e.bubbles, cancelable:e.cancelable });
			});
		}

		// On écoute les changements dans le slot (Items statiques ou ajoutés via JS)
		this.#_ui.slot.addEventListener(
			'slotchange',
			this.#_handleSlotChange.bind(this),
		);

		this.#_handleSlotChange();

		if (this.loading) this.#_setLoading();
	}

	protected _p_update(
		name: string,
		_: string | null,
		newVal: string | null,
	): void | Nullable<'break'> {
		switch (name) {
			case ATTRIBUTE_LOADING:
				if (newVal === null) this.#_cardPart.removeAttribute(ATTRIBUTE_LOADING);
				else this.#_setLoading({ attributeValue: newVal });
				break;
		}
	}
	//#endregion Lifecycle

	//#region Public methods
	/**
	 * Ajoute des éléments.
	 *
	 * Note: On ajoute simplement au Light DOM. Le slotchange détectera l'ajout et déclenchera le tri.
	 * @param content Elements à ajouter
	 */
	public add(...content: HTMLBnumCardItemMail[]): this {
		this.append(...content);
		return this;
	}

	/**
	 * Vide le composant.
	 */
	public clear(): this {
		this.innerHTML = EMPTY_STRING; // Vide le Light DOM
		return this;
	}
	//#endregion Public methods

	//#region Private methods
	/**
	 * Met la card en mode loading
	 * @param param0 
	 */
	#_setLoading({ attributeValue = EMPTY_STRING }: {attributeValue?: string | boolean} = {}) {
		this.#_cardPart.setAttribute(
			ATTRIBUTE_LOADING,
			attributeValue as unknown as string ?? EMPTY_STRING,
		);
	}

	/**
	 * Gère le tri des éléments.
	 * Utilise requestAnimationFrame pour ne pas bloquer le thread si beaucoup d'items.
	 */
	#_handleSlotChange(): void {
		if (this.#_isSorting) return;

		this.#_sortChildren();
	}

	/**
	 * Tri les éléments enfants de la liste par date décroissante.
	 */
	@RenderFrame()
	#_sortChildren(): void {
		// 1. Récupérer les éléments assignés au slot (Uniquement les Nodes Elements, pas le texte)
		const elements = this.#_ui.slot.assignedElements() as HTMLElement[];

		// Filtrer pour être sûr de ne trier que des mails (sécurité)
		const mailItems = elements.filter(el =>
			el.tagName.toLowerCase().includes(HTMLBnumCardItemMail.TAG),
		) as HTMLBnumCardItemMail[];

		if (mailItems.length === 0) {
			this.#_ui.noElements.hidden = false;
			this.#_ui.slot.hidden = true;
			return;
		} else {
			this.#_ui.noElements.hidden = true;
			this.#_ui.slot.hidden = false;
		}

		if (mailItems.length < 2) return; // Pas besoin de trier

		// 2. Vérifier si un tri est nécessaire (optimisation)
		let isSorted = true;
		for (let i = 0; i < mailItems.length - 1; i++) {
			if (this.#_getDate(mailItems[i]) < this.#_getDate(mailItems[i + 1])) {
				isSorted = false;
				break;
			}
		}
		if (isSorted) return;

		// 3. Trier en mémoire
		this.#_isSorting = true; // Verrouiller pour éviter que le déplacement ne relance slotchange

		mailItems.sort((a, b) => {
			// Tri décroissant (le plus récent en haut)
			return this.#_getDate(b) - this.#_getDate(a);
		});

		// 4. Réinsérer dans l'ordre via un Fragment (1 seul Reflow)
		const fragment = document.createDocumentFragment();
		mailItems.forEach(item => fragment.appendChild(item));
		this.appendChild(fragment); // Déplace les éléments existants, ne les recrée pas.

		// Notifier le changement
		this.onElementChanged.call(mailItems);

		// Déverrouiller après que le microtask de mutation soit passé
		setTimeout(() => {
			this.#_isSorting = false;
		}, 0);
	}

	/**
	 * Helper pour parser la date de manière robuste
	 */
	#_getDate(item: HTMLBnumCardItemMail): number {
		const dateStr = item.getAttribute(ATTRIBUTE_DATA_DATE);
		if (!dateStr) return item.date.getTime();
		if (dateStr === 'now') return Date.now();
		return new Date(dateStr).getTime();
	}
	//#endregion Private methods

	//#region Static methods
	/**
	 * Méthode statique pour créer une instance du composant.
	 * @param param0 Options de création
	 * @param param0.contents Contenus initiaux à ajouter
	 * @param param0.url URL du titre
	 * @returns Nouvelle node HTMLBnumCardEmail
	 */
	static Create({
		contents = [],
		url = EMPTY_STRING,
	}: {
		contents?: HTMLBnumCardItemMail[];
		url?: string;
	} = {}): HTMLBnumCardEmail {
		const node = document.createElement(this.TAG) as HTMLBnumCardEmail;

		if (url !== EMPTY_STRING) node.setAttribute(ATTRIBUTE_DATA_URL, url);
		if (contents.length > 0) node.add(...contents);

		return node;
	}

	/**
	 * Liste des évènements disponibles pour cet éléments
	 */
	static get Events(): Readonly<{TITLE_URL_CLICKED: Readonly<typeof EVENT_URL_TITLE_CLICK>, CHANGE:Readonly<typeof CHANGE_EVENT>}> {
		return {
			TITLE_URL_CLICKED: EVENT_URL_TITLE_CLICK,
			CHANGE: CHANGE_EVENT
		};
	}
	//#endregion Static methods
}
